import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, get, useForm } from "react-hook-form";

import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { CommentsField } from "../../../../../shared/fields/CommentsField";
import {
  CATALOG_GRADE_INITIAL_VALUES,
  CatalogGrade,
  GradeChemistryLimit,
} from "../../../models/CatalogGrade";
import {
  getCatGradeChemistryLimits,
  upsertCatalogGrade,
} from "../../../repositories/CatalogGradeRepository";
import { AddElementModal } from "./AddElementModal";
import { CopyFromModal } from "./CopyFromModal";
import { ElementLimits } from "./ElementLimits";

type Props = {
  grade: CatalogGrade | null;
  modal: "new" | "edit" | "delete" | "";
  refreshData: Function;
  onHide: ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => void;
};

export const GradeDetails = ({ grade, modal, refreshData, onHide }: Props) => {
  const [message, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCopyFromModal, setShowCopyFromModal] = useState<boolean>(false);
  const [showNewElementModal, setShowNewElementModal] =
    useState<boolean>(false);
  const [chemistriesElements, setChemistriesElements] = useState<
    GradeChemistryLimit[]
  >([]);

  const [elementsSelected, setElementsSelected] = useState<
    GradeChemistryLimit[]
  >([]);

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    const resp = await getCatGradeChemistryLimits(grade?.GradeId!);
    if (resp.ok) {
      const rows = get(
        resp,
        "data.tables[0].rows",
        [],
      ) as GradeChemistryLimit[];
      setChemistriesElements(rows);
      setElementsSelected(rows);
    } else {
      setError(resp.message);
    }
    setIsLoading(false);
  };

  const {
    reset,
    getValues,
    handleSubmit,
    control,
    formState: { dirtyFields },
  } = useForm<CatalogGrade>({
    mode: "onChange",
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || modal === "new";

  const onSubmit: SubmitHandler<CatalogGrade> = async (data) => {
    setIsLoading(true);
    if (modal !== "delete") {
      const gradeResp = await upsertCatalogGrade({
        ...data,
        GradeId: data.IsDuplicate ? null : data.GradeId,
        GradeName: data.GradeName.trimEnd(),
      });
      if (gradeResp.ok) {
        const entityId = get(gradeResp, "data.tables[0].rows[0].Id", 0);
        setNotificationMessage("The grade was saved succesfully.");
        await refreshData();
        onHide({
          shouldUpdate: true,
          close: false,
          entityId,
        });
      } else {
        setError(gradeResp.message);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (modal !== "") {
      reset(grade || CATALOG_GRADE_INITIAL_VALUES);
      loadData().then(() => {});
      setIsLoading(false);
    }
  }, [modal, grade]);

  return (
    <>
      {grade === null && modal !== "new" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a grade or create a new one</Typography>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              style={{ margin: "0px 15px" }}
            >
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                style={{
                  margin: "5px 0px",
                }}
              >
                <Grid item md={3}>
                  <Controller
                    name="GradeName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Name"
                        variant="outlined"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item md={3}>
                  <Controller
                    name="Author"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Author"
                        variant="outlined"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item md={3}>
                  <Controller
                    name="DateTimeModified"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Updated Time"
                        variant="outlined"
                        value={
                          dxtToLocalServerTime(field.value, "MM/dd/yy HH:mm") ??
                          ""
                        }
                        onChange={field.onChange}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item md={3} style={{ marginTop: 5 }}>
                  <Controller
                    name="Enabled"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={Boolean(field.value)}
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                          id="enabled-checkbox"
                        />
                        <Typography
                          component="label"
                          htmlFor="enabled-checkbox"
                          style={{ marginLeft: 8, cursor: "pointer" }}
                        >
                          Enabled
                        </Typography>
                      </div>
                    )}
                  />
                </Grid>
                <Grid item md={4}></Grid>
                <Grid item md={2} style={{ marginLeft: -20 }}>
                  <Typography align="center">MIN</Typography>
                </Grid>
                <Grid item md={2} style={{ marginLeft: -5 }}>
                  <Typography align="center">MAX</Typography>
                </Grid>
                <Grid item md={2} style={{ marginLeft: -5 }}>
                  <Typography align="center">AIM</Typography>
                </Grid>
                <Grid item md={2}></Grid>
              </Grid>
              <Grid
                item
                alignItems="center"
                justifyContent="flex-start"
                style={{
                  overflowY: "auto",
                  height: "42vh",
                }}
              >
                {isLoading ? (
                  <CenteredLazyLoading />
                ) : (
                  chemistriesElements.map((e, index) => (
                    <Grid item>
                      <ElementLimits
                        element={e}
                        disabled={modal !== "edit"}
                        refreshData={() => {
                          refreshData();
                          loadData();
                        }}
                        setError={setError}
                        setNotificationMessage={setNotificationMessage}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
              <Grid item xs={12} md={12} style={{ marginTop: 10 }}>
                <Controller
                  name="Comments"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CommentsField
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      fieldError={error}
                      getValues={getValues}
                      disabled={modal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              alignItems="center"
              style={{ margin: "0px 15px" }}
            >
              <Grid item md={1} xs={12}>
                <IconButton
                  color="primary"
                  onClick={() => setShowNewElementModal(true)}
                  title="Add new element"
                >
                  <AddIcon />
                </IconButton>
              </Grid>
              <Grid item md={5} xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowCopyFromModal(true)}
                >
                  Copy Chemistries Limits
                </Button>
              </Grid>
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => onHide({ shouldUpdate: false, close: true })}
                >
                  Close
                </Button>
              </Grid>
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  startIcon={isLoading && <CircularProgress size="1rem" />}
                  disabled={isLoading || !isFormDirty}
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
      <CopyFromModal
        gradeId={grade?.GradeId!}
        show={showCopyFromModal}
        onHide={(id?: number | null) => {
          if (id) {
            refreshData();
          }
          setShowCopyFromModal(false);
        }}
        setNotificationMessage={setNotificationMessage}
        setError={setError}
      />
      <AddElementModal
        gradeId={grade?.GradeId!}
        show={showNewElementModal}
        onHide={(newElements: boolean) => {
          if (newElements) {
            refreshData();
          }
          setShowNewElementModal(false);
        }}
        onSelectionChanged={setElementsSelected}
        elementsOfGrade={chemistriesElements}
        elementsSelected={elementsSelected}
        setNotificationMessage={setNotificationMessage}
        setError={setError}
      />
    </>
  );
};
